import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

export const INITIAL_PASSWORD_MIN_LENGTH = 12;

export type AuthCallbackKind = 'invite' | 'recovery' | null;

export interface AuthCallbackPayload {
  kind: AuthCallbackKind;
  errorCode: string | null;
  hasAuthParameters: boolean;
}

export function parseAuthCallback(url: string): AuthCallbackPayload {
  const parsed = new URL(url);
  const hash = new URLSearchParams(parsed.hash.replace(/^#/, ''));
  const type = hash.get('type') ?? parsed.searchParams.get('type');
  const kind = type === 'recovery' ? 'recovery' : type === 'invite' || type === 'signup' ? 'invite' : null;
  const errorCode = hash.get('error_code') ?? parsed.searchParams.get('error_code');

  return {
    kind,
    errorCode,
    hasAuthParameters: Boolean(
      hash.get('access_token') ||
      hash.get('refresh_token') ||
      parsed.searchParams.get('code') ||
      type ||
      hash.get('error') ||
      parsed.searchParams.get('error'),
    ),
  };
}

export function sanitizeAuthCallbackUrl(url: string): string {
  const parsed = new URL(url);
  parsed.hash = '';
  for (const parameter of ['code', 'type', 'error', 'error_code', 'error_description']) {
    parsed.searchParams.delete(parameter);
  }
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export function getAuthCallbackKind(url: string): AuthCallbackKind {
  return parseAuthCallback(url).kind;
}

export function shouldRequestInitialPassword({
  event,
  session,
  hasProfile,
  callbackKind,
}: {
  event: AuthChangeEvent;
  session: Session | null;
  hasProfile: boolean;
  callbackKind: AuthCallbackKind;
}): boolean {
  if (!session) return false;
  if (event === 'PASSWORD_RECOVERY' || callbackKind === 'recovery' || callbackKind === 'invite') return true;
  return !hasProfile && session.user.user_metadata?.dlx_password_initialized !== true;
}

export function validateInitialPassword(password: string, confirmation: string): string | null {
  if (password.length < INITIAL_PASSWORD_MIN_LENGTH) {
    return `Use pelo menos ${INITIAL_PASSWORD_MIN_LENGTH} caracteres.`;
  }
  if (password !== confirmation) return 'As senhas precisam ser iguais.';
  return null;
}
